import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getParticipantThreadMessages, ParticipantThreadMessage } from '../../services/participantThreads';
import { CognitiveTraceDetail } from '../common/CognitiveTraceDetail';

interface ParticipantThreadViewProps {
  sessionId: string;
  avatarId: string;
  participantId: string;
  avatarName: string;
  participantName: string;
}

const formatTime = (timestamp: string) => {
  try {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return timestamp;
  }
};

export const ParticipantThreadView: React.FC<ParticipantThreadViewProps> = ({
  sessionId,
  avatarId,
  participantId,
  avatarName,
  participantName,
}) => {
  const [messages, setMessages] = useState<ParticipantThreadMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getParticipantThreadMessages(sessionId, avatarId, participantId)
      .then(msgs => { if (!cancelled) setMessages(msgs); })
      .catch(() => { if (!cancelled) setError('Failed to load thread messages'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionId, avatarId, participantId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  if (error) {
    return <div className="text-sm text-red-600 p-4">{error}</div>;
  }

  if (messages.length === 0) {
    return <div className="text-sm text-slate-400 p-4">No messages in this thread yet.</div>;
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      {messages.map(msg => {
        const isAvatar = msg.speaker === 'avatar';
        return (
          <div key={msg.messageId} className={`flex flex-col ${isAvatar ? 'items-start' : 'items-end'}`}>
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-snug ${
                isAvatar
                  ? 'bg-slate-50 border border-slate-200 text-slate-800'
                  : 'bg-primary/10 border border-primary/20 text-navy'
              }`}
            >
              <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1">
                {isAvatar ? avatarName : participantName}
              </div>
              <p>{msg.text}</p>
              {isAvatar && <CognitiveTraceDetail {...msg} />}
            </div>
            <span className="text-[11px] text-slate-400 mt-1 px-1">{formatTime(msg.timestamp)}</span>
          </div>
        );
      })}
    </div>
  );
};

export default ParticipantThreadView;
